import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConnectedProps, connect } from "react-redux";

import { useAppSelector } from "~/app/store";
import MultiPort from "~/features/autostoreBin/MultiPort";
import CompartmentDataCard from "~/features/autostorePutaway/CompartmentDataCard";

import {
  getHoldTypeOptions,
  holdTypeCheck,
  isAutostoreView,
  HoldType,
  HoldTypeSource
} from "~/lib/helpers";

import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { getVariantData } from "~/redux/actions/inventory";
import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";
import { NextBinInventory } from "~/redux/reducers/autostore";

import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectThisWorkstation,
  selectIsShortSideBin
} from "~/redux/selectors/workstationsSelectors";
import { useHoldBinMutation } from "~/redux/warehouse/bin.hooks";

import {
  useDeleteInventoryHoldMutation,
  useGetInventoryWithVariantInfoByAutostoreBinNumberQuery
} from "~/redux/warehouse/inventory.hooks";
import { NextBinResponse, NextEmptyBinResponse } from "~/types/api";

const connector = connect(undefined, {
  getVariantData
});

type Props = ConnectedProps<typeof connector> & {
  isOpen: boolean;
  onClose: () => void;
  onCallback?: () => void;
  currentSelectedBin: NextEmptyBinResponse | NextBinResponse | null;
  currentSelectedCompartment?: number | null;
  nextEmptyBinByPort: {
    [portId: number]: NextEmptyBinResponse | null;
  };
  nextBinInventoryByPort?: {
    [portId: number]: NextBinInventory;
  };
};

function InventoryHoldModal(props: Props) {
  const {
    isOpen,
    onClose,
    onCallback,
    nextEmptyBinByPort,
    nextBinInventoryByPort,
    currentSelectedBin = null,
    currentSelectedCompartment = null,
    getVariantData
  } = props;
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const trackedModalName = useMixpanelModalName(
    "Select Compartment to Place on Hold"
  );

  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const { ap_excludeRecalledHold } = useAppSelector(selectServerConfig);
  const [holdBin, { isLoading: holdBinLoading }] = useHoldBinMutation();
  const [removeHold] = useDeleteInventoryHoldMutation();

  const workstation = useAppSelector(selectThisWorkstation);

  /** The list of possible hold types, including Damaged, Out Of Stock, and potentially Recalled. */
  const possibleHolds = getHoldTypeOptions({
    excludeRecalledHold: ap_excludeRecalledHold,
    includeOutOfStockHold: true
  });

  const [newSelectedBin, setNewSelectedBin] =
    useState<NextEmptyBinResponse | null>(null);
  const [newSelectedCompartment, setNewSelectedCompartment] = useState<
    number | null
  >(null);

  const newOrCurrentSelectedBin =
    newSelectedBin !== null ? newSelectedBin : currentSelectedBin;

  const newOrCurrentSelectedCompartment =
    newSelectedCompartment !== null
      ? newSelectedCompartment
      : currentSelectedCompartment;

  const { compartmentData } =
    useGetInventoryWithVariantInfoByAutostoreBinNumberQuery(
      workstation && newOrCurrentSelectedBin && isOpen
        ? {
            autostoreGridId: workstation.autostoreGridId,
            binNumber: newOrCurrentSelectedBin.openBinResponse.binId
          }
        : skipToken,
      {
        selectFromResult: ({ data, isLoading }) => ({
          isLoading,
          compartmentData:
            newOrCurrentSelectedCompartment !== null
              ? data?.find(
                  (inv) =>
                    inv.inventory.bin.autostoreCompartmentNumber ===
                    newOrCurrentSelectedCompartment + 1
                )
              : undefined
        })
      }
    );

  const [selectedHolds, setSelectedHolds] = useState<HoldType[]>([]);

  const availableHolds = possibleHolds.filter((h) =>
    selectedHolds.every((sh) => sh.reasonCode !== h.reasonCode)
  );

  useEffect(() => {
    setSelectedHolds(
      compartmentData?.inventory.holds.map((hold) => ({
        reasonCode: hold.reasonCode as InventoryHoldReasonCode,
        source: hold.source as HoldTypeSource
      })) ?? []
    );
  }, [compartmentData, setSelectedHolds, t]);

  const selectedCompartmentBinId =
    newOrCurrentSelectedCompartment !== null &&
    newOrCurrentSelectedBin?.autostoreBinCompartments.find(
      (compartment) =>
        compartment.autostoreCompartmentNumber ===
        newOrCurrentSelectedCompartment + 1
    )?.binId;

  const selectedCompartmentInventory =
    newOrCurrentSelectedCompartment !== null &&
    newOrCurrentSelectedBin?.inventory.find(
      (inv) =>
        inv.bin.autostoreCompartmentNumber ===
        newOrCurrentSelectedCompartment + 1
    );

  const holdsHaveBeenAdded =
    selectedHolds?.filter((selectedHold) =>
      compartmentData?.inventory.holds.every(
        (h) => h.reasonCode !== selectedHold.reasonCode
      )
    ).length > 0;
  const holdsHaveBeenRemoved =
    compartmentData?.inventory.holds.filter(
      (currentHold) =>
        !selectedHolds.find((h) => h.reasonCode === currentHold.reasonCode)
    )?.length ?? 0 > 0;

  const canPlaceHold =
    !holdBinLoading &&
    selectedCompartmentInventory &&
    newOrCurrentSelectedCompartment !== null &&
    newOrCurrentSelectedBin?.autostoreBinCompartments.length &&
    (holdsHaveBeenAdded || holdsHaveBeenRemoved);

  const handleSelectHold = (selectedHold: HoldType) => {
    setSelectedHolds([...selectedHolds, selectedHold]);
  };

  const handleUnselectHold = (unselectedHold: HoldType) => {
    if (
      holdTypeCheck(
        unselectedHold,
        errorToast,
        compartmentData?.inventory.expiration
          ? dayjs(compartmentData.inventory.expiration)
          : undefined
      )
    ) {
      return;
    }

    setSelectedHolds([
      ...selectedHolds.filter((h) => h.reasonCode !== unselectedHold.reasonCode)
    ]);
  };

  const handleConfirm = async () => {
    if (workstation && selectedCompartmentBinId && compartmentData) {
      const { portId } = newOrCurrentSelectedBin.openBinResponse;
      const { autostoreGridId, id: workstationId } = workstation;
      const addedHolds = selectedHolds.filter((selectedHold) =>
        compartmentData.inventory.holds.every(
          (h) => h.reasonCode !== selectedHold.reasonCode
        )
      );
      for (const addedHold of addedHolds) {
        try {
          await holdBin({
            warehouseBinId: selectedCompartmentBinId,
            reasonCode: addedHold.reasonCode?.toLocaleLowerCase(),
            gridId: autostoreGridId,
            portId,
            workstationId
          }).unwrap();
          successToast(t("placed hold", { code: t(addedHold.reasonCode) }));
        } catch (e) {
          errorToast(getMessageFromRtkError(e), {
            trackingLocation: trackedModalName
          });
        }
      }
      // holds currently on inventory
      const currentHolds =
        compartmentData?.inventory.holds.map((hold) => hold.reasonCode) ?? [];
      // holds that are on the inventory but are no longer selected in the UI
      const removedHolds = currentHolds.filter((currentHold) => {
        return !selectedHolds.find((h) => h.reasonCode === currentHold);
      });

      for (const removedHold of removedHolds) {
        try {
          await removeHold({
            inventoryId: compartmentData.inventory.inventoryId,
            workstationId: workstation.id,
            reasonCode: removedHold,
            autostoreGridId: workstation.autostoreGridId,
            autostorePortId: newOrCurrentSelectedBin?.openBinResponse.portId
          }).unwrap();
          successToast(
            t("removed hold", {
              code: t(removedHold as InventoryHoldReasonCode)
            })
          );
        } catch (e) {
          errorToast(getMessageFromRtkError(e), {
            trackingLocation: trackedModalName
          });
        }
      }

      if (onCallback) onCallback();
      onClose();
    }
  };

  const ConfirmButton = (
    <Button
      id="confirm-button"
      size="large"
      sx={{ width: "100%", fontWeight: "normal" }}
      startIcon={<CheckIcon />}
      onClick={() => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Button Click",
          label: "Confirm",
          eventProperties: {
            BinId: newOrCurrentSelectedBin?.openBinResponse.binId,
            Compartment: newOrCurrentSelectedCompartment,
            "Selected Holds": selectedHolds.map((h) => h.reasonCode),
            Product: compartmentData?.variant
          }
        });
        void handleConfirm();
      }}
      disabled={!canPlaceHold}
    >
      {t("confirm")}
    </Button>
  );

  // inventory hold placement fields
  const currentHoldTextField = (
    <List sx={{ flexGrow: 1 }}>
      {selectedHolds.map((h) => (
        <ListItem
          data-reasoncode={h.reasonCode}
          key={h.reasonCode}
          onClick={() => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Select Option",
              label: "Remove Hold",
              eventProperties: {
                "Hold Type": h.reasonCode
              }
            });
            handleUnselectHold(h);
          }}
          secondaryAction={
            <IconButton>
              <HighlightOffIcon />
            </IconButton>
          }
          sx={{
            border: "1px solid",
            borderRadius: 1,
            p: 1,
            mb: 1
          }}
        >
          <ListItemText
            primary={
              <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                {t(h.reasonCode)}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>
  );

  const filteredHoldTextField = (
    <TextField
      id="filteredHold"
      label={t("add hold")}
      type="string"
      select
      fullWidth
      variant="outlined"
      value=""
      InputLabelProps={{
        shrink: true
      }}
      inputProps={{
        sx: { fontSize: "1rem", flexGrow: 1 }
      }}
      // if there are no more holds to be selected, disable the field
      disabled={!availableHolds.length}
    >
      {availableHolds.map((item) => (
        <MenuItem
          key={item.reasonCode}
          value={item.reasonCode.toLocaleLowerCase()}
          onClick={() => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Select Option",
              label: "Add Hold",
              eventProperties: {
                "Hold Type": item.reasonCode
              }
            });
            handleSelectHold(item);
          }}
        >
          {t(item.reasonCode)}
          <IconButton>
            <AddCircleOutlineIcon />
          </IconButton>
        </MenuItem>
      ))}
    </TextField>
  );

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!isOpen) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setNewSelectedCompartment(null);
    }
  }, [isOpen]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (workstation?.autostoreGridId && compartmentData && isOpen) {
      // TODO: refactor out, side effect of `inventoryToModify` dependency in cycle counts
      void getVariantData({
        variantId: compartmentData.inventory.variantId,
        binId: compartmentData.inventory.bin.binId,
        limit: 50,
        offset: 0,
        autostoreGridId: isAutostoreView(window.location.search)
          ? workstation.autostoreGridId
          : undefined,
        inventoryId: compartmentData.inventory.inventoryId
      });
    }
  }, [compartmentData, getVariantData, workstation, isOpen]);

  const closeDialog = () => {
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Close Modal"
    });
    onClose();
    setNewSelectedCompartment(null);
    setNewSelectedBin(null);
  };

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="xl">
      <DialogTitle>{t("select compartment to place on hold")}</DialogTitle>
      <DialogContent sx={{ mx: 4 }}>
        <Stack gap={3}>
          <MultiPort
            nextEmptyBinByPort={nextEmptyBinByPort}
            nextBinInventoryByPort={nextBinInventoryByPort}
            workstation={workstation}
            onBinClick={(compartment: number | undefined) => {
              if (compartment || compartment === 0)
                setNewSelectedCompartment(compartment);
            }}
            setSelectedBinCallback={(
              bin: NextEmptyBinResponse | NextBinResponse
            ) => setNewSelectedBin(bin)}
            selectedCompartment={newOrCurrentSelectedCompartment ?? undefined}
            currentSelectedBinId={
              newOrCurrentSelectedBin?.openBinResponse.binId
            }
            allowSelectingCompartmentsWithInventoryOnly
            isShortSideBin={isShortSideBin}
            trackedPageName={trackedModalName}
          />

          <Stack
            direction="row"
            gap={5}
            justifyContent="center"
            alignItems="center"
          >
            <CompartmentDataCard
              compartmentData={compartmentData}
              selectedBinNumber={newOrCurrentSelectedBin?.openBinResponse.binId}
              selectedCompartment={newOrCurrentSelectedCompartment ?? undefined}
            />
            <Stack gap={1} flexBasis={200}>
              {currentHoldTextField}
              {filteredHoldTextField}
              {ConfirmButton}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default connector(InventoryHoldModal);
